import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal } from 'lucide-react';
import { api, STAGE_VALUES, type LineStage, type RfqListResponse } from '@/lib/api';
import { RfqRow } from '@/components/rfq-row';
import { RfqBoard } from '@/components/rfq-board';
import { FlagToggle } from '@/components/flag-toggle';
import { useToggleRfqFlag } from '@/lib/use-toggle-rfq-flag';
import { STAGE_LABEL } from '@/lib/format';
import { AcceptedAwaitingActionPanel } from '@/components/accepted-awaiting-action';
import { BuyersWaitingPanel } from '@/components/buyers-waiting';
import { SOURCING_CHANNELS, SOURCING_CHANNEL_VALUES, type SourcingChannel } from '@shared/sourcing-channels';

type SourcingFilter = '' | 'unposted' | SourcingChannel;

interface Props {
  selectedRfqId: string | null;
}

// Fields are 16px on a phone (iOS zooms the page on focus otherwise) and compact from sm up.
const FILTER_SELECT = 'h-10 min-w-0 rounded-md border border-border bg-background px-2 text-base sm:h-8 sm:rounded sm:py-1 sm:text-xs';

export function RfqList({ selectedRfqId }: Props) {
  const { getToken } = useAuth();
  // 'all' = the chronological keyset list. 'board' = the manually ordered
  // flagged focus list. Two genuinely different queries, so a tab rather than
  // another checkbox: the existing filter row composes (AOG + urgent + …),
  // whereas board mode replaces the ordering wholesale and cannot paginate.
  const [tab, setTab] = useState<'all' | 'board'>('all');
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<LineStage | ''>('');
  const [aogOnly, setAogOnly] = useState(false);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [quoteSentOnly, setQuoteSentOnly] = useState(false);
  const [sourcing, setSourcing] = useState<SourcingFilter>('');
  // Phones: filters fold behind a button so the list is not pushed off-screen. From sm up they are always shown.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (stage) params.set('stage', stage);
  if (aogOnly) params.set('aog', 'true');
  if (urgentOnly) params.set('urgent', 'true');
  if (flaggedOnly) params.set('flagged', 'true');
  if (quoteSentOnly) params.set('quoteSent', 'true');
  if (sourcing === 'unposted') params.set('unpostedOnly', 'true');
  else if (sourcing) params.set('sourcingChannel', sourcing);
  const queryString = params.toString();

  // Infinite/keyset pagination — the API returns 50 RFQs + a nextCursor
  // (received_at). Without consuming the cursor the list could only ever show
  // the newest 50, which silently hid older RFQs once a busy day filled the
  // first page. "Load more" walks the cursor back through full history.
  const {
    data, isLoading, isError, error,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['rfqs', queryString],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(queryString);
      if (pageParam) p.set('cursor', pageParam as string);
      const qs = p.toString();
      return api<RfqListResponse>(`/api/rfqs${qs ? `?${qs}` : ''}`, {
        tokenGetter: () => getToken(),
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 10_000,
  });

  const rfqs = data?.pages.flatMap((p) => p.rfqs) ?? [];

  const activeFilters = [stage, aogOnly, urgentOnly, flaggedOnly, quoteSentOnly, sourcing].filter(Boolean).length;
  const clearFilters = () => {
    setStage(''); setAogOnly(false); setUrgentOnly(false); setFlaggedOnly(false); setQuoteSentOnly(false); setSourcing('');
  };

  // One shared mutation for the whole list. `variables.id` identifies which row
  // is mid-flight, so only that toggle dims rather than all of them.
  const toggleFlag = useToggleRfqFlag();

  // Infinite scroll: observe a sentinel at the bottom of the scroll container
  // and fetch the next page as it nears view (200px early so it feels seamless).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rfqs.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-3 flex items-center gap-1" role="tablist" aria-label="RFQ views">
        {([['all', 'All RFQs'], ['board', '🚩 Priority']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors min-h-[36px] ' +
              (tab === key
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50')
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'board' ? (
        <RfqBoard selectedRfqId={selectedRfqId} />
      ) : (
      <>
      <div className="px-4 py-3 border-b border-border space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PN, RFQ #, PO #…"
              aria-label="Search RFQs"
              title="Search part numbers, RFQ #, PO #, quote # and invoice #"
              // 16px on a phone: iOS Safari zooms the page when a smaller field is focused.
              className="h-10 w-full min-w-0 rounded-md border border-border bg-background pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/40 sm:h-9 sm:text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="rfq-filters"
            className="relative inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted sm:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            Filters
            {activeFilters > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
                {activeFilters}
              </span>
            )}
          </button>
        </div>
        <div
          id="rfq-filters"
          className={`${filtersOpen ? 'grid' : 'hidden'} grid-cols-2 gap-x-3 gap-y-1 sm:flex sm:flex-wrap sm:items-center sm:gap-2`}
        >
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as LineStage | '')}
            aria-label="Stage"
            className={`${FILTER_SELECT} order-1 sm:order-1`}
          >
            <option value="">All stages</option>
            {STAGE_VALUES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL[s]}</option>
            ))}
          </select>
          <select
            value={sourcing}
            onChange={(e) => setSourcing(e.target.value as SourcingFilter)}
            aria-label="Sourcing"
            className={`${FILTER_SELECT} order-2 sm:order-6`}
            title="Filter by where the RFQ has been posted"
          >
            <option value="">Any sourcing</option>
            <option value="unposted">Not yet posted</option>
            {SOURCING_CHANNEL_VALUES.map((c) => (
              <option key={c} value={c}>Posted to {SOURCING_CHANNELS[c].label}</option>
            ))}
          </select>
          {([
            ['AOG only', aogOnly, setAogOnly, 'sm:order-2'],
            ['Urgent only', urgentOnly, setUrgentOnly, 'sm:order-3'],
            ['🚩 Flagged', flaggedOnly, setFlaggedOnly, 'sm:order-4'],
            ['✅ Quote sent', quoteSentOnly, setQuoteSentOnly, 'sm:order-5'],
          ] as const).map(([label, checked, set, desktopOrder]) => (
            <label key={label} className={`order-3 ${desktopOrder} flex min-h-10 cursor-pointer items-center gap-2 text-sm sm:min-h-0 sm:gap-1.5 sm:text-xs`}>
              <input type="checkbox" checked={checked} onChange={(e) => set(e.target.checked)} className="h-4 w-4 rounded" />
              {label}
            </label>
          ))}
          {activeFilters > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="order-4 col-span-2 inline-flex h-10 items-center justify-center rounded-md text-sm font-medium text-primary hover:bg-muted sm:order-7 sm:h-auto sm:px-2 sm:py-1 sm:text-xs"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto">
        {/* Pinned above the keyset list rather than sorted into it: the cursor
            walks received_at and cannot continue a list whose head was hoisted
            by a different key. Same reason the flagged board is its own tab. */}
        <AcceptedAwaitingActionPanel variant="rail" />
        <BuyersWaitingPanel variant="rail" />

        {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {isError && (
          <div className="p-6 text-sm text-destructive">
            {(error as { message?: string })?.message ?? 'Failed to load RFQs'}
          </div>
        )}
        {!isLoading && rfqs.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">
            {q || stage || aogOnly || urgentOnly || flaggedOnly || quoteSentOnly || sourcing ? 'No RFQs match your filters.' : 'No RFQs yet. Click "+ New" to parse one.'}
          </div>
        )}
        <ul>
          {rfqs.map((rfq) => (
            <li key={rfq.id}>
              <RfqRow
                rfq={rfq}
                isSelected={rfq.id === selectedRfqId}
                flagToggle={
                  <FlagToggle
                    isFlagged={rfq.isFlagged}
                    // One mutation serves every row, so `variables` scopes both
                    // states to the row that actually triggered it. KNOWN LIMIT:
                    // `variables` holds only the MOST RECENT toggle, so clicking
                    // two rows in quick succession clears the first row's
                    // spinner early. Both still reconcile on settle; per-row
                    // mutation instances would fix it and cost more than the
                    // symptom is worth.
                    isPending={toggleFlag.isPending && toggleFlag.variables?.id === rfq.id}
                    isError={toggleFlag.isError && toggleFlag.variables?.id === rfq.id}
                    onToggle={() => toggleFlag.mutate({ id: rfq.id, isFlagged: !rfq.isFlagged })}
                  />
                }
              />
            </li>
          ))}
        </ul>
        {/* Infinite-scroll sentinel + status */}
        <div ref={sentinelRef} />
        {isFetchingNextPage && (
          <div className="p-3 text-center text-xs text-muted-foreground">Loading more…</div>
        )}
        {!hasNextPage && rfqs.length > 0 && (
          <div className="p-3 text-center text-xs text-muted-foreground">
            {rfqs.length} RFQ{rfqs.length === 1 ? '' : 's'} · end of history
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
